/* «Награды и задания» — данные и расчёты (перенос assets/bot-rewards.js, разделы 1–3; описание раздела — в шапке того файла и APP-SPEC-6-pages).
   [ДЕМО] Размеры наград, список заданий, условия серии и челленджей придуманы для прототипа. [АДМИНКА] Задания и награды — настройки команды.
   [БЭКЕНД] Состояние серии, заданий, прогресс и сброс — с сервера (GET /app/rewards, docs/03-api.md §10.11); здесь — как в прототипе. */
import { uiFormat, uiPlural } from '../../i18n';

export type TabKey = 'day' | 'one' | 'fr';
export type TaskTab = 'day' | 'week' | 'one' | 'fr';
export type TaskState = 'todo' | 'claim' | 'done';
export type Go = 'studio' | 'circle' | 'tts' | 'screen' | 'history' | 'clone' | 'story' | 'chat' | 'sid' | 'ref' | 'nav';

/** Задание с сервера: состояние и прогресс считает сервер, клиент их не выводит */
export interface ServerTaskState {
  st: TaskState;
  pv: number;
  /** Почему выполнить нельзя (пусто — можно) */
  off: string;
  /** Куда ведёт задание: раздел (ui.go) или экран студии (ui.openApp); null — некуда */
  route: { go: string } | { app: Record<string, unknown> } | null;
  tpl?: string;
}

export interface Task {
  id: string;
  tab: TaskTab;
  /** Значок из реестра; svg — готовая разметка (сторис, логотип STEOS ID) */
  ic?: string;
  svg?: string;
  t: string;
  s?: string;
  r: number;
  n?: number;
  src?: 'act' | 'buy' | 'sid';
  go: Go;
  o?: Record<string, unknown>;
  sv?: ServerTaskState;
}

export interface Tier {
  name: string;
  pct: number;
  from: number;
}

/** Снимок сервера для страницы: серия, друзья и уровни партнёра; ошибки и незавершённое начисление */
export interface ServerView {
  ready: boolean;
  err: string;
  busy: boolean;
  /** Исход прежнего начисления не подтверждён: новые начисления заблокированы до проверки */
  pending: boolean;
  blocked: boolean;
  days: number[];
  dayAmt: number;
  nextAmt: number;
  /** Дней подряд по серверу */
  streakN: number;
  friendsKnown?: boolean;
  /** Сброс заданий дня и недели, мс; null — сервер не прислал */
  dayAt: number | null;
  weekAt: number | null;
  /** Партнёрские числа; null — партнёрские данные не пришли, блок не показывается */
  pt: { act: number; buy: number; earn: number; tiers: Tier[] } | null;
}

/** Награда за день серии, C: дни 1…7. После седьмого дня серия идёт по кругу, пропуск дня начинает её с первого дня */
export const DAYS = [5, 5, 10, 10, 15, 20, 50];
export const TABS: [TabKey, string][] = [
  ['day', 'Ежедневные'],
  ['one', 'Разовые'],
  ['fr', 'Друзья'],
];

/** Задания (как TASKS прототипа); значки сторис и STEOS ID — из общего ядра, поэтому список строится после загрузки BotCore */
export function buildTasks(BC: BotCoreApi): Task[] {
  return [
    { id: 'works', tab: 'day', ic: 'sparkle', t: 'Создайте 3 работы', r: 15, n: 3, go: 'studio' },
    { id: 'dcircle', tab: 'day', ic: 'send', t: 'Сделайте кружок', s: 'Персонаж или вы сами', r: 10, go: 'circle' },
    { id: 'dtts', tab: 'day', ic: 'wave', t: 'Озвучьте текст', s: 'Голосом героя или клона', r: 5, go: 'tts' },
    {
      id: 'prompt',
      tab: 'day',
      ic: 'wand',
      t: 'Улучшите описание',
      s: 'Бот допишет детали',
      r: 5,
      go: 'screen',
      o: { screen: 'text', tool: 'pfix' },
    },
    { id: 'share', tab: 'day', ic: 'share', t: 'Отправьте работу', s: 'Из «Истории» в любой чат', r: 10, go: 'history' },
    { id: 'wcircles', tab: 'week', ic: 'send', t: 'Неделя кружков', s: 'Создайте 10 кружков за неделю', r: 150, n: 10, go: 'circle' },
    {
      id: 'wtools',
      tab: 'week',
      ic: 'grid4',
      t: 'Исследователь',
      s: 'Попробуйте 5 разных инструментов студии',
      r: 100,
      n: 5,
      go: 'studio',
    },
    { id: 'circle', tab: 'one', ic: 'send', t: 'Первый кружок', s: 'Видеокружок для Telegram', r: 30, go: 'circle' },
    { id: 'clone', tab: 'one', ic: 'mic', t: 'Клон голоса', s: 'Всего 2 секунды речи', r: 50, go: 'clone' },
    { id: 'photo', tab: 'one', ic: 'image', t: 'Первое фото', s: 'Портрет в любом стиле', r: 20, go: 'screen', o: { screen: 'photo' } },
    {
      id: 'reels',
      tab: 'one',
      ic: 'film',
      t: 'Первый рилс с вами',
      s: 'Шаблон одной кнопкой',
      r: 40,
      go: 'screen',
      o: { screen: 'reels' },
    },
    { id: 'cover', tab: 'one', ic: 'music', t: 'Первый кавер', s: 'Песня другим голосом', r: 40, go: 'screen', o: { screen: 'cover' } },
    { id: 'avatar', tab: 'one', ic: 'face', t: 'Свой аватар', s: 'Кружки и видео с вами', r: 100, go: 'screen', o: { screen: 'avatar' } },
    { id: 'story', tab: 'one', svg: BC.story.ICON, t: 'Сторис в Telegram', s: 'С вашей ссылкой', r: 50, go: 'story' },
    { id: 'chat', tab: 'one', ic: 'usersPlus', t: 'Бот в чате', s: 'Добавьте бота в группу', r: 30, go: 'chat' },
    { id: 'sid', tab: 'one', svg: BC.sid.logo('rw-lg'), t: 'Вход в STEOS ID', s: 'Один аккаунт с сайтом', r: 50, go: 'sid', src: 'sid' },
    { id: 'f1', tab: 'fr', ic: 'usersPlus', t: 'Первый друг', r: 30, n: 1, src: 'act', go: 'ref' },
    { id: 'f3', tab: 'fr', ic: 'users', t: '3 друга', r: 100, n: 3, src: 'act', go: 'ref' },
    { id: 'f10', tab: 'fr', ic: 'users', t: '10 друзей', r: 300, n: 10, src: 'act', go: 'ref' },
    { id: 'f25', tab: 'fr', ic: 'crown', t: '25 друзей', r: 1000, n: 25, src: 'act', go: 'ref' },
    { id: 'fbuy', tab: 'fr', ic: 'coins', t: 'Первый покупатель', s: 'Первая оплата друга', r: 100, src: 'buy', go: 'ref' },
  ];
}

/** Прогресс заданий без src [ДЕМО]: сегодня — озвучка и кружок, «Создайте 3 работы» 2 из 3 */
export const PG0: Record<string, number> = {
  works: 2,
  dcircle: 1,
  dtts: 1,
  prompt: 0,
  share: 0,
  wcircles: 6,
  wtools: 3,
  circle: 1,
  clone: 1,
  photo: 0,
  reels: 0,
  cover: 0,
  avatar: 0,
  story: 0,
  chat: 0,
};
/** Награды, которые уже забраны [ДЕМО] */
export const GOT0 = ['dtts', 'circle', 'f1', 'f3', 'fbuy'];

/** Уровни партнёрской программы — как в «Партнёрстве» (bot-partner.js DATA.tiers) */
export const tiers = (REF: BotCoreApi['REF']) => [
  { name: 'Базовый', pct: REF.pct, from: 0 },
  { name: 'Продвинутый', pct: 7, from: 10 },
  { name: 'Амбассадор', pct: REF.max, from: 50 },
];
/** По уровню (0 — Базовый, 1 — Продвинутый, 2 — Амбассадор): покупатели, начислено C, приглашённые с первой работой */
export const PT = { buy: [4, 12, 54], earn: [8640, 17280, 43200], act: [9, 18, 71] };

/** Состояние раздела — тот же набор полей, что S прототипа (его читают другие модули: BotProfile) */
export interface RewardsState {
  streak: number;
  today: boolean;
  tab: TabKey;
  pg: Record<string, number>;
  got: Set<string>;
  story: boolean;
  sid: boolean;
  copied: string;
  hold: boolean;
  toPt: boolean;
  /** Серверный режим: снимок сервера; null — прототип */
  srv: ServerView | null;
}

export const initialState = (): RewardsState => ({
  streak: 3,
  today: false,
  tab: 'day',
  pg: { ...PG0 },
  got: new Set(GOT0),
  story: false,
  sid: false,
  copied: '',
  hold: false,
  toPt: false,
  srv: null,
});

/** Расчёты над состоянием и общими данными ядра (уровень «Партнёрства», вход STEOS ID) */
export function calc(S: RewardsState, TASKS: Task[], BC: BotCoreApi) {
  const PS = () => window.BotPartner?.S || { lvl: 0, fresh: false };
  const lvl = () => Math.max(0, Math.min(2, PS().lvl | 0));
  /** В серверном режиме демо-числа не подставляются: нет партнёрских данных — нули */
  const pt = () => (S.srv ? (S.srv.pt ?? { act: 0, buy: 0, earn: 0, tiers: [] }) : null);
  const buyersN = () => pt()?.buy ?? (PS().fresh ? 0 : PT.buy[lvl()]);
  const earnN = () => pt()?.earn ?? (PS().fresh ? 0 : PT.earn[lvl()]);
  const actN = () => pt()?.act ?? (PS().fresh ? 0 : PT.act[lvl()]);
  const goal = (t: Task) => t.n || 1;
  const prog = (t: Task) =>
    t.sv
      ? t.sv.pv
      : t.src === 'act'
        ? actN()
        : t.src === 'buy'
          ? buyersN() > 0
            ? 1
            : 0
          : t.src === 'sid'
            ? BC.sid.linked
              ? 1
              : 0
            : S.pg[t.id] || 0;
  const stOf = (t: Task): TaskState => t.sv?.st ?? (prog(t) < goal(t) ? 'todo' : S.got.has(t.id) ? 'done' : 'claim');
  const tasksOf = (k: TaskTab) => TASKS.filter(t => t.tab === k);
  const claimN = (k: TabKey) =>
    TASKS.filter(t => (k === 'day' ? t.tab === 'day' || t.tab === 'week' : t.tab === k) && stOf(t) === 'claim').length;
  const gotN = (L: Task[]) => L.filter(t => stOf(t) === 'done').length;
  const TIERS0 = tiers(BC.REF);
  const tiersNow = (): Tier[] => pt()?.tiers ?? TIERS0;
  const tierI = (b: number) => tiersNow().reduce((a, t, i) => (b >= t.from ? i : a), 0);
  /** Награды дней серии: с сервера или прототипа */
  const days = () => (S.srv ? S.srv.days : DAYS);
  /** Сегодняшняя награда серии и завтрашняя */
  const dayAmt = () => S.srv?.dayAmt ?? DAYS[S.streak];
  const nextAmt = () => S.srv?.nextAmt ?? DAYS[S.streak % DAYS.length];
  const streakN = () => S.srv?.streakN ?? S.streak;
  /** Сводка для меню «Ещё» (app.html menuRw): сколько наград ждут, их сумма, дней серии */
  const summary = () => {
    if (S.srv && !S.srv.ready) return { wait: 0, sum: 0, streak: 0 };
    const L = TASKS.filter(t => stOf(t) === 'claim');
    const day = !S.today && S.streak < days().length ? dayAmt() : 0;
    return { wait: L.length + (day ? 1 : 0), sum: L.reduce((a, t) => a + t.r, 0) + day, streak: streakN() };
  };
  return { buyersN, earnN, actN, goal, prog, stOf, tasksOf, claimN, gotN, tiers: tiersNow, tierI, days, dayAmt, nextAmt, streakN, summary };
}
export type Calc = ReturnType<typeof calc>;

/** До обновления заданий дня — полночь у пользователя или время сброса с сервера (at, мс): «7 ч», «40 мин» */
export function leftDay(at?: number | null): string {
  const d = new Date();
  const m = new Date(d);
  m.setHours(24, 0, 0, 0);
  const min = Math.max(1, Math.ceil(((at ?? +m) - +d) / 6e4));
  return min < 60 ? uiFormat('{0} мин', min) : uiFormat('{0} ч', Math.floor(min / 60));
}
/** До конца челленджей недели — понедельник 00:00 или сброс с сервера (at, мс): «Ещё 3 дня», в последние сутки — «Последний день» */
export function leftWeek(at?: number | null): string {
  const g = new Date().getDay();
  const n = at != null ? Math.max(1, Math.ceil((at - Date.now()) / 864e5)) : g === 0 ? 1 : 8 - g;
  return n === 1 ? uiFormat('Последний день') : uiFormat('Ещё {0} {1}', n, uiPlural(n, 'день', 'дня', 'дней'));
}
